/**
 * RC Review Dialog Component
 * 
 * Displays extracted data from RC document and allows manual corrections.
 * Shows confidence scores and highlights fields that may need review.
 */

import { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Edit2, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { RCExtractedData, LegalForm } from '@lebusiness/types';

interface RCReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractedData: RCExtractedData;
  onConfirm: (data: RCExtractedData) => void;
  onCancel: () => void;
}

const LEGAL_FORMS: { value: LegalForm; label: string }[] = [
  { value: 'EURL', label: 'EURL - Entreprise Unipersonnelle à Responsabilité Limitée' },
  { value: 'SARL', label: 'SARL - Société à Responsabilité Limitée' },
  { value: 'SPA', label: 'SPA - Société Par Actions' },
  { value: 'SNC', label: 'SNC - Société en Nom Collectif' },
  { value: 'SCS', label: 'SCS - Société en Commandite Simple' },
  { value: 'EI', label: 'EI - Entreprise Individuelle' },
  { value: 'Auto-Entrepreneur', label: 'Auto-Entrepreneur' },
];

export function RCReviewDialog({
  open,
  onOpenChange,
  extractedData,
  onConfirm,
  onCancel,
}: RCReviewDialogProps) {
  const [data, setData] = useState<RCExtractedData>(extractedData);
  const [editingField, setEditingField] = useState<string | null>(null);

  useEffect(() => {
    setData(extractedData);
  }, [extractedData]);

  const handleFieldChange = (field: keyof RCExtractedData, value: any) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirm = () => {
    onConfirm(data);
  };

  const getConfidenceColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 0.9) return 'text-green-600';
    if (score >= 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (score?: number) => {
    if (!score) return 'Inconnu';
    if (score >= 0.9) return 'Excellente';
    if (score >= 0.7) return 'Bonne';
    return 'Faible - Vérifier';
  };

  const confidenceScore = data.confidence_score || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Vérifier les données extraites
          </DialogTitle>
          <DialogDescription>
            Vérifiez et corrigez les informations extraites de votre RC. Les champs surlignés en jaune
            nécessitent votre attention.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Confidence Score */}
          <div className="rounded-lg border bg-gray-50 p-4 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Confiance de l'extraction:</span>
                <span className={`text-sm font-bold ${getConfidenceColor(confidenceScore)}`}>
                  {(confidenceScore * 100).toFixed(0)}% - {getConfidenceLabel(confidenceScore)}
                </span>
              </div>
              {confidenceScore < 0.7 && (
                <div className="flex items-center gap-1 text-sm text-yellow-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Vérification recommandée</span>
                </div>
              )}
            </div>
          </div>

          {/* Company Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name_fr">Nom de l'entreprise (Français)</Label>
              <div className="relative mt-1">
                <Input
                  id="company_name_fr"
                  value={data.company_name_fr || ''}
                  onChange={(e) => handleFieldChange('company_name_fr', e.target.value)}
                  placeholder="Nom de l'entreprise"
                  className={!data.company_name_fr ? 'border-yellow-500' : ''}
                />
                {!data.company_name_fr && (
                  <AlertTriangle className="absolute right-3 top-2.5 h-4 w-4 text-yellow-500" />
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="company_name_ar">Nom de l'entreprise (Arabe)</Label>
              <Input
                id="company_name_ar"
                value={data.company_name_ar || ''}
                onChange={(e) => handleFieldChange('company_name_ar', e.target.value)}
                placeholder="اسم الشركة"
                dir="rtl"
                className="text-right"
              />
            </div>
          </div>

          {/* Legal Form */}
          <div>
            <Label htmlFor="legal_form">Forme juridique</Label>
            <Select
              value={data.legal_form_fr}
              onValueChange={(value) => handleFieldChange('legal_form_fr', value)}
            >
              <SelectTrigger className={!data.legal_form_fr ? 'border-yellow-500' : ''}>
                <SelectValue placeholder="Sélectionnez la forme juridique" />
              </SelectTrigger>
              <SelectContent>
                {LEGAL_FORMS.map((form) => (
                  <SelectItem key={form.value} value={form.value}>
                    {form.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!data.legal_form_fr && (
              <p className="mt-1 text-xs text-yellow-600">Champ requis</p>
            )}
          </div>

          {/* RC Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rc_number">Numéro RC</Label>
              <div className="relative mt-1">
                <Input
                  id="rc_number"
                  value={data.rc_number || ''}
                  onChange={(e) => handleFieldChange('rc_number', e.target.value)}
                  placeholder="XX/XX-XXXXXXX"
                  className={!data.rc_number ? 'border-yellow-500' : ''}
                />
                {!data.rc_number && (
                  <AlertTriangle className="absolute right-3 top-2.5 h-4 w-4 text-yellow-500" />
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="wilaya">Wilaya</Label>
              <Input
                id="wilaya"
                value={data.wilaya || ''}
                onChange={(e) => handleFieldChange('wilaya', e.target.value)}
                placeholder="Wilaya"
              />
            </div>
          </div>

          {/* NIF & NIS */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nif">NIF (Numéro d'Identification Fiscale)</Label>
              <Input
                id="nif"
                value={data.nif || ''}
                onChange={(e) => handleFieldChange('nif', e.target.value)}
                placeholder="15-20 chiffres"
                maxLength={20}
              />
            </div>
            <div>
              <Label htmlFor="nis">NIS (Numéro d'Identification Statistique)</Label>
              <Input
                id="nis"
                value={data.nis || ''}
                onChange={(e) => handleFieldChange('nis', e.target.value)}
                placeholder="15-20 chiffres"
                maxLength={20}
              />
            </div>
          </div>

          {/* Activity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="activity_fr">Activité (Français)</Label>
              <Input
                id="activity_fr"
                value={data.activity_fr || ''}
                onChange={(e) => handleFieldChange('activity_fr', e.target.value)}
                placeholder="Ex: Commerce en gros"
              />
            </div>
            <div>
              <Label htmlFor="activity_ar">Activité (Arabe)</Label>
              <Input
                id="activity_ar"
                value={data.activity_ar || ''}
                onChange={(e) => handleFieldChange('activity_ar', e.target.value)}
                placeholder="مثال: تجارة الجملة"
                dir="rtl"
                className="text-right"
              />
            </div>
          </div>

          {/* Capital */}
          <div>
            <Label htmlFor="capital">Capital social (DZD)</Label>
            <Input
              id="capital"
              type="number"
              value={data.capital || ''}
              onChange={(e) => handleFieldChange('capital', parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              step="0.01"
            />
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="address">Adresse du siège social</Label>
            <Input
              id="address"
              value={data.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder="Adresse complète"
            />
          </div>

          {/* Gérant */}
          <div>
            <Label htmlFor="gerant_name">Nom du gérant</Label>
            <Input
              id="gerant_name"
              value={data.gerant_name || ''}
              onChange={(e) => handleFieldChange('gerant_name', e.target.value)}
              placeholder="Nom et prénom"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="button" onClick={handleConfirm}>
            <Save className="mr-2 h-4 w-4" />
            Confirmer et continuer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
